import { put, select, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';

import { entityActions } from './entity.slice';
import { EntityDetailsPayload } from 'common/models/data/entityStore.model';
import { EntityService } from './entity.service';
import { ApiInitiator, EntityType } from 'common/enums/data.enum';
import { ApiResult, FieldError } from 'common/models';
import { IndicatorVM } from 'common/models/data/indicator.model';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { CategoryDTO, EntityDetails } from 'common/models/data/entity.model';
import { getFieldErrors } from '../preview-data/previewData.selector';

const getEntityDetailsById = function* ({ payload }: PayloadAction<EntityDetailsPayload>) {
    const entityDetailsResponse: ApiResult<EntityDetails | IErrorApiResponse> =
        yield EntityService.getEntityDetails(payload);

    if (entityDetailsResponse.hasErrors && entityDetailsResponse.errors) {
        yield put({
            type: entityActions.fetchEntityDetailsError.type,
            payload: entityDetailsResponse.errors
        });
    } else {
        yield put({
            type: entityActions.fetchEntityDetailsSuccess.type,
            payload: entityDetailsResponse.value
        });
    }
};

const getEntityDetailsByIdWatch = function* () {
    yield takeLatest(entityActions.fetchEntityDetails.type, getEntityDetailsById);
};

const getCategoryById = function* ({ payload }: PayloadAction<EntityDetailsPayload>) {
    let entityDetailsResponse = {
        hasErrors: false,
        errors: [],
        value: {
            categoryId: 'eaef75b9-e829-4b2b-ab02-00286e3e4152',
            categoryName: 'Sectors',
            categoryOrder: 0,
            subCategoryId: 'eaef75b9-e829-4b2b-ab02-00286e3e4152',
            subCategoryName: '',
            order: 1,
            description: ''
        }
    };
    if (payload.initiator !== ApiInitiator.ArchiveList) {
        entityDetailsResponse = yield EntityService.getCategoryDetailsById(payload);
    }
    if (entityDetailsResponse.hasErrors && entityDetailsResponse.errors) {
        yield put({
            type: entityActions.fetchCategoryDetailsForEditError.type,
            payload: entityDetailsResponse.errors
        });
    } else {
        let entityValue: CategoryDTO = entityDetailsResponse.value;
        if (
            payload.entityType === EntityType.SubCategory &&
            payload.initiator === ApiInitiator.MasterData
        ) {
            entityValue = {
                categoryId: entityDetailsResponse.value.subCategoryId,
                categoryName: entityDetailsResponse.value.subCategoryName,
                categoryOrder: entityDetailsResponse.value.order,
                description: entityDetailsResponse.value.description
            };
        }
        yield put({
            type: entityActions.fetchCategoryDetailsForEditSuccess.type,
            payload: entityValue
        });
    }
};

const getCategoryByIdWatch = function* () {
    yield takeLatest(entityActions.fetchCategoryDetailsForEdit.type, getCategoryById);
};

const getIndicatorById = function* ({ payload }: PayloadAction<EntityDetailsPayload>): any {
    const indicatorResponse = yield EntityService.getIndicatorDetailsById(payload);

    if (indicatorResponse && indicatorResponse.hasErrors && indicatorResponse.errors) {
        yield put({
            type: entityActions.fetchIndicatorDetailsForEditError.type,
            payload: indicatorResponse.errors
        });
    } else {
        if (payload.initiator === undefined) {
            const errorData = yield select(getFieldErrors);
            if (errorData && errorData.length > 0) {
                if (indicatorResponse?.value) {
                    const errorIndicatorCountry = (loc: any) =>
                        errorData.findIndex((fl: FieldError) => fl.field === loc.locationName) > -1;
                    indicatorResponse.value.locations.forEach((loc: any) => {
                        loc.hasError = errorIndicatorCountry(loc);
                    });
                }
            }
        }
        yield put({
            type: entityActions.fetchIndicatorDetailsForEditSuccess.type,
            payload: indicatorResponse?.value
        });
    }
};

const getIndicatorByIdWatch = function* () {
    yield takeLatest(entityActions.fetchIndicatorDetailsForEdit.type, getIndicatorById);
};

const getSources = function* ({ payload }: PayloadAction<ApiInitiator>) {
    const { hasErrors, errors, value }: ApiResult<any> = yield EntityService.getSources(payload);
    if (hasErrors && errors) {
        const error: string = errors[0];
        yield put({
            type: entityActions.fetchSourcesFailed.type,
            payload: error
        });
    } else {
        yield put({
            type: entityActions.fetchSourcesSuccess.type,
            payload: value
        });
    }
};

const getSourcesWatch = function* () {
    yield takeLatest(entityActions.fetchSources.type, getSources);
};

export { getEntityDetailsByIdWatch, getCategoryByIdWatch, getIndicatorByIdWatch, getSourcesWatch };
